import * as React from 'react';
import AppTabs from '../../components/AppTabs';
import { FC, useCallback, useEffect, useState } from 'react';
import { Alert, Card, CardBody, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import StyledTable, { PrimaryText, SecondaryText } from '../../components/StyledTable';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'connected-react-router';
import moment from 'moment';
import styled from 'styled-components';
import DeleteIcon from '@material-ui/icons/Delete';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import SettingsIcon from '@material-ui/icons/Settings';
import IconButton from '@material-ui/core/IconButton';
import { Tabs, Tab } from '@material-ui/core';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import { PermissionEnum, WaitingListItem, WaitingListPriority } from '../../models';
import { addToWaitingListAsync, getWaitingListAsync, removeFromWaitingListAsync } from '../../ajax/requests';
import { useGeneralSettings, useServicesSettings } from '../../store/settings/hooks';
import { serviceName } from '../../shared/appointmint/appointment';
import { getEntitiesDataItems } from '../../store/entities/selectors';
import { ProfessionalEntity } from '../../store/entities/state';
import { ApplicationState } from '../../store';
import { AutocompleteTextField } from '../../components/Forms/fields';
import Typography from '../../components/Typography';
import WaitingListItemForm from '../../components/Forms/WaitingListItemForm';
import { paths } from '../../app/constants';
import ToolTip from '../../components/ToolTip';
import { getMyUser } from '../../store/session/selectors';
import { useQueryParam } from '../../hooks/useQueryParam';
import WaitingListInsights from './WaitingListInsights';
import { KpiRow, KpiCard, KpiValue, KpiLabel } from './WaitingListKpiCards';
import AppButton from '../../components/AppButton';

const getProfessionals = getEntitiesDataItems<ProfessionalEntity>('professionals');

type WaitingListTab = 'list' | 'insights';


const ClientLink = styled.button<React.ButtonHTMLAttributes<HTMLButtonElement>>`
    border: 0;
    padding: 0;
    background: none;
    color: var(--text-muted);
    font-size: 0.85rem;
    font-weight: 400;
    line-height: 1.35;
    text-align: left;

    &:hover,
    &:focus {
        color: var(--brand-secondary);
        text-decoration: none;
    }
`;

const SecondaryCellText = styled(SecondaryText)<React.HTMLAttributes<HTMLSpanElement>>`
    display: inline-flex;
    align-items: center;
    min-height: 1.9rem;
`;

const PriorityBadge = styled.span<React.HTMLAttributes<HTMLSpanElement> & { priority: WaitingListPriority }>`
    display: inline-flex;
    align-items: center;
    padding: 0.12rem 0.45rem;
    border-radius: 999px;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.01em;
    line-height: 1.2;
    ${({ priority }) => {
        switch (priority) {
            case WaitingListPriority.High:
                return `
                    background-color: var(--warning-bg);
                    color: var(--warning-color-strong);
                    border: var(--border-thin) solid var(--warning-border);
                `;
            case WaitingListPriority.Urgent:
                return `
                    background-color: var(--danger-bg);
                    color: var(--danger-color-strong);
                    border: var(--border-thin) solid var(--danger-border);
                `;
            default:
                return `
                    background-color: var(--surface-brand-soft);
                    color: var(--text-muted);
                    border: var(--border-thin) solid var(--surface-border-subtle);
                `;
        }
    }}
`;

const ExpiredBadge = styled.span`
    display: inline-flex;
    align-items: center;
    margin-left: var(--spacer-XS);
    padding: 0.12rem 0.45rem;
    border-radius: 999px;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.01em;
    line-height: 1.2;
    background-color: var(--danger-bg);
    color: var(--danger-color-strong);
    border: var(--border-thin) solid var(--danger-border);
`;

const DescriptionAlert = styled(Alert)`
    margin-bottom: var(--spacer-M);
`;

const GlobalCountersRow = styled(KpiRow)`
    margin-top: var(--spacer-S);
`;

const RowActions = styled.div`
    display: flex;
    align-items: center;
    gap: var(--spacer-XXS);
    white-space: nowrap;
`;

const TooltipContent = styled.div`
    font-size: 0.85rem;
    color: var(--text-muted);
`;

const HeaderActions = styled.div`
    display: flex;
    align-items: center;
    gap: var(--spacer-XS);
`;

const WaitingLists: FC = () => {
    const t = useTranslations();
    const dispatch = useDispatch();
    const generalSettings = useGeneralSettings();
    const servicesSettings = useServicesSettings();
    const professionals = useSelector((state: ApplicationState) => getProfessionals(state));
    const myUser = useSelector((state: ApplicationState) => getMyUser(state));
    const [tab, setTab] = useQueryParam<WaitingListTab>('tab', 'list');

    const [items, setItems] = useState<WaitingListItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [filterService, setFilterService] = useState('');
    const [filterProfessional, setFilterProfessional] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [formValue, setFormValue] = useState<Partial<WaitingListItem>>({});
    const [saving, setSaving] = useState(false);

    const serviceKeys = Object.keys(servicesSettings);
    const loggedProfessional = professionals.find(p => p.id === myUser?.professionalId);
    const isProfessionalUser = !!myUser?.professionalId;
    const hasWaitingListGlobalAccess = myUser?.permissions.some(p => [
        PermissionEnum.Administrator,
        PermissionEnum.AppointmentsAccess,
        PermissionEnum.AppointmentsManagement,
    ].includes(p)) || false;
    const canEditWaitingListSettings = myUser?.permissions.includes(PermissionEnum.Administrator) ?? false;
    const hasScopedManagedProfessionals = isProfessionalUser
        && !hasWaitingListGlobalAccess
        && professionals.some(p => p.id !== myUser?.professionalId);
    const isOwnScopeProfessional = isProfessionalUser && !hasWaitingListGlobalAccess && !hasScopedManagedProfessionals;
    const professionalFilterValue = isOwnScopeProfessional
        ? (loggedProfessional?.name ?? '')
        : hasScopedManagedProfessionals
            ? (filterProfessional || loggedProfessional?.name || '')
            : filterProfessional;

    const selectedProfessional = professionals.find(p => p.name === professionalFilterValue);
    const selectedServiceId = serviceKeys.find(s => serviceName(s, generalSettings) === filterService);
    const scopedProfessionalId = selectedProfessional?.id ?? myUser?.professionalId;
    const queryProfessionalId = isOwnScopeProfessional
        ? myUser?.professionalId
        : hasScopedManagedProfessionals
            ? scopedProfessionalId
            : selectedProfessional?.id;

    const loadItems = useCallback(() => {
        setLoading(true);
        getWaitingListAsync(selectedServiceId, queryProfessionalId)
            .then(setItems)
            .finally(() => setLoading(false));
    }, [queryProfessionalId, selectedServiceId]);

    useEffect(() => {
        loadItems();
    }, [loadItems]);

    const openModal = useCallback(() => {
        if (isOwnScopeProfessional || hasScopedManagedProfessionals) {
            setFormValue({
                professionalId: scopedProfessionalId,
                professionalName: selectedProfessional?.name ?? loggedProfessional?.name,
            });
        } else {
            setFormValue({});
        }
        setModalOpen(true);
    }, [hasScopedManagedProfessionals, isOwnScopeProfessional, loggedProfessional?.name, scopedProfessionalId, selectedProfessional?.name]);

    const handleAdd = useCallback(async () => {
        if (!formValue.clientId || !formValue.service) return;
        setSaving(true);
        try {
            const waitingListItem = formValue as WaitingListItem;
            const itemToAdd: WaitingListItem = isOwnScopeProfessional
                ? {
                    ...waitingListItem,
                    professionalId: myUser?.professionalId,
                    professionalName: loggedProfessional?.name,
                }
                : hasScopedManagedProfessionals
                    ? {
                        ...waitingListItem,
                        professionalId: waitingListItem.professionalId ?? scopedProfessionalId,
                        professionalName: waitingListItem.professionalName ?? selectedProfessional?.name ?? loggedProfessional?.name,
                    }
                    : waitingListItem;
            await addToWaitingListAsync(itemToAdd);
            loadItems();
            setModalOpen(false);
        } finally {
            setSaving(false);
        }
    }, [
        formValue,
        hasScopedManagedProfessionals,
        isOwnScopeProfessional,
        loadItems,
        loggedProfessional?.name,
        myUser?.professionalId,
        scopedProfessionalId,
        selectedProfessional?.name,
    ]);

    const handleRemove = useCallback((item: WaitingListItem) => async () => {
        await removeFromWaitingListAsync(item.clientId, item.service, item.professionalId);
        setItems(prev => prev.filter(i =>
            !(i.clientId === item.clientId && i.service === item.service && i.professionalId === item.professionalId)
        ));
    }, []);

    const goToClient = useCallback((clientId: string) => {
        dispatch(push({ pathname: paths.clients(), search: `?id=${clientId}` }));
    }, [dispatch]);

    const goToWaitingListSettings = useCallback(() => {
        dispatch(push(paths.waitingListSettings()));
    }, [dispatch]);

    const getPriorityLabel = (priority: WaitingListPriority) => {
        switch (priority) {
            case WaitingListPriority.High: return t(TK.priorityHigh);
            case WaitingListPriority.Urgent: return t(TK.priorityUrgent);
            default: return t(TK.priorityNormal);
        }
    };

    const isExpired = (limitDate?: string) => {
        if (!limitDate) return false;
        return moment(limitDate).isBefore(moment(), 'day');
    };

    const getWaitingForLabel = (addedAt?: string) => {
        if (!addedAt) return '—';
        const daysWaiting = Math.max(0, moment().startOf('day').diff(moment(addedAt).startOf('day'), 'days'));
        return t(TK.waitingDaysShort, String(daysWaiting));
    };

    const totalEntries = items.length;
    const highPriorityCount = items.filter(i => i.priority === WaitingListPriority.High || i.priority === WaitingListPriority.Urgent).length;
    const oldestDays = items.length > 0
        ? Math.max(...items.map(i => i.addedAt ? moment().startOf('day').diff(moment(i.addedAt).startOf('day'), 'days') : 0))
        : 0;
    const expiringCount = items.filter(i => isExpired(i.limitDate)).length;

    return (
        <Card>
            <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 style={{ margin: 0 }}>{t(TK.waitingLists)}</h4>
                <HeaderActions>
                    {canEditWaitingListSettings && (
                        <>
                            <IconButton size="small" id="waiting-list-settings-button" onClick={goToWaitingListSettings}>
                                <SettingsIcon fontSize="small" />
                            </IconButton>
                            <ToolTip
                                target="waiting-list-settings-button"
                                toolTipContent={t(TK.settings)}
                            />
                        </>
                    )}
                    {tab === 'list' && (
                        <AppButton color="success" onClick={openModal}>{t(TK.addToWaitingList)}</AppButton>
                    )}
                </HeaderActions>
            </div>

            <AppTabs value={tab} onChange={(_, v) => setTab(v)} indicatorColor="primary" textColor="primary" style={{ borderBottom: 'var(--border-thin) solid var(--appLigthGrey)' }}>
                <Tab label={t(TK.list)} value="list" />
                <Tab label={t(TK.insights)} value="insights" />
            </AppTabs>

            <div>
                {tab === 'list' && (
                    <>
                        <GlobalCountersRow>
                                <KpiCard>
                                    <KpiValue>{totalEntries}</KpiValue>
                                    <KpiLabel>{t(TK.total)}</KpiLabel>
                                </KpiCard>
                                <KpiCard>
                                    <KpiValue>{highPriorityCount}</KpiValue>
                                    <KpiLabel>{t(TK.highPriorityEntries)}</KpiLabel>
                                </KpiCard>
                                <KpiCard>
                                    <KpiValue>{oldestDays}</KpiValue>
                                    <KpiLabel>{t(TK.oldestEntry)} ({t(TK.days)})</KpiLabel>
                                </KpiCard>
                                <KpiCard>
                                    <KpiValue>{expiringCount}</KpiValue>
                                    <KpiLabel>{t(TK.expiringEntries)}</KpiLabel>
                                </KpiCard>
                            </GlobalCountersRow>

                            <DescriptionAlert color="secondary">
                                {t(TK.waitingListsCancellationPromptInfo)}
                            </DescriptionAlert>

                            <div className="d-flex flex-wrap" style={{ gap: 'var(--spacer-S)', marginBottom: 'var(--spacer-S)' }}>
                                <div style={{ minWidth: 200 }}>
                                    <AutocompleteTextField
                                        id="waiting-lists-filter-service"
                                        label={t(TK.service)}
                                        value={filterService}
                                        options={serviceKeys.map(s => serviceName(s, generalSettings))}
                                        onValueChanged={setFilterService}
                                    />
                                </div>
                                <div style={{ minWidth: 200 }}>
                                    <AutocompleteTextField
                                        id="waiting-lists-filter-professional"
                                        label={t(TK.professional)}
                                        value={professionalFilterValue}
                                        options={professionals.map(p => p.name)}
                                        onValueChanged={setFilterProfessional}
                                        disabled={isOwnScopeProfessional}
                                    />
                                </div>
                            </div>

                            {loading
                                ? <Typography><i>{t(TK.loading)}...</i></Typography>
                                : !items.length
                                    ? <Typography><i>{t(TK.noEntries)}</i></Typography>
                                    : (
                                        <StyledTable scrollable hover>
                                            <thead>
                                                <tr>
                                                    <th>{t(TK.client)}</th>
                                                    <th>{t(TK.service)}</th>
                                                    <th>{t(TK.professional)}</th>
                                                    <th>{t(TK.priority)}</th>
                                                    <th>{t(TK.waitingTime)}</th>
                                                    <th>{t(TK.limitDate)}</th>
                                                    <th>{t(TK.notes)}</th>
                                                    <th aria-label={t(TK.action)} />
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {items.map((item, idx) => (
                                                    <tr key={idx}>
                                                        <td>
                                                            <ClientLink type="button" onClick={() => goToClient(item.clientId)}>
                                                                <PrimaryText>{item.clientName}</PrimaryText>
                                                            </ClientLink>
                                                        </td>
                                                        <td><SecondaryCellText>{serviceName(item.service, generalSettings)}</SecondaryCellText></td>
                                                        <td><SecondaryCellText>{item.professionalName ?? '—'}</SecondaryCellText></td>
                                                        <td>
                                                            <PriorityBadge priority={item.priority ?? WaitingListPriority.Normal}>
                                                                {getPriorityLabel(item.priority ?? WaitingListPriority.Normal)}
                                                            </PriorityBadge>
                                                        </td>
                                                        <td><SecondaryCellText>{getWaitingForLabel(item.addedAt)}</SecondaryCellText></td>
                                                        <td>
                                                            {item.limitDate ? (
                                                                <SecondaryCellText style={{ color: isExpired(item.limitDate) ? 'var(--danger-color-strong)' : undefined }}>
                                                                    {moment(item.limitDate).format('DD/MM/YYYY')}
                                                                    {isExpired(item.limitDate) && (
                                                                        <ExpiredBadge>{t(TK.expired)}</ExpiredBadge>
                                                                    )}
                                                                </SecondaryCellText>
                                                            ) : <SecondaryCellText>—</SecondaryCellText>}
                                                        </td>
                                                        <td><SecondaryCellText>{item.notes || '—'}</SecondaryCellText></td>
                                                        <td>
                                                            <RowActions>
                                                                <IconButton size="small" id={`waiting-list-item-info-${idx}`}>
                                                                    <InfoOutlinedIcon fontSize="small" />
                                                                </IconButton>
                                                                <ToolTip
                                                                    target={`waiting-list-item-info-${idx}`}
                                                                    toolTipContent={
                                                                        <TooltipContent>
                                                                            <div>
                                                                                <b>{t(TK.addedAt)}:</b>{' '}
                                                                                {item.addedAt ? moment(item.addedAt).format('DD/MM/YYYY HH:mm') : '—'}
                                                                            </div>
                                                                            <div>
                                                                                <b>{t(TK.addedBy)}:</b>{' '}
                                                                                {item.addedByName || item.addedByUsername || '—'}
                                                                            </div>
                                                                        </TooltipContent>
                                                                    }
                                                                />
                                                                <IconButton size="small" onClick={handleRemove(item)}>
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            </RowActions>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </StyledTable>
                                    )
                            }
                        </>
                    )}
                {tab === 'insights' && <WaitingListInsights />}
            </div>

            <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)}>
                <ModalHeader toggle={() => setModalOpen(false)}>{t(TK.addToWaitingList)}</ModalHeader>
                <ModalBody>
                    <WaitingListItemForm value={formValue} onChange={setFormValue} />
                </ModalBody>
                <ModalFooter>
                    <AppButton
                        color="success"
                        disabled={!formValue.clientId || !formValue.service || saving}
                        onClick={handleAdd}
                    >
                        {t(TK.add)}
                    </AppButton>
                    <AppButton color="secondary" onClick={() => setModalOpen(false)}>
                        {t(TK.cancel)}
                    </AppButton>
                </ModalFooter>
            </Modal>
            </CardBody>
        </Card>
    );
};

export default WaitingLists;
